import { getSiteMessageUnreadCount } from '@/services/notification/siteMessage';

/** 站内信未读数轮询间隔（毫秒） */
export const SITE_MESSAGE_UNREAD_POLL_INTERVAL_MS = 30_000;

const SITE_MESSAGE_UNREAD_DATA_CHANGED_EVENT =
  'site-message-unread-data-changed';

let cachedUnreadCount = 0;
let unreadCountInitialized = false;
let refreshPromise: Promise<number> | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollRefCount = 0;

const unreadCountListeners = new Set<(count: number) => void>();

export function getCachedSiteMessageUnreadCount() {
  return cachedUnreadCount;
}

export function subscribeSiteMessageUnreadCount(
  listener: (count: number) => void,
) {
  unreadCountListeners.add(listener);
  if (unreadCountInitialized) {
    listener(cachedUnreadCount);
  }
  return () => {
    unreadCountListeners.delete(listener);
  };
}

function notifySiteMessageUnreadDataChanged() {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(SITE_MESSAGE_UNREAD_DATA_CHANGED_EVENT));
}

/** 未读数变化时触发，用于刷新未读/全部列表 */
export function subscribeSiteMessageUnreadDataChanged(listener: () => void) {
  if (typeof window === 'undefined') {
    return () => {};
  }
  window.addEventListener(SITE_MESSAGE_UNREAD_DATA_CHANGED_EVENT, listener);
  return () =>
    window.removeEventListener(
      SITE_MESSAGE_UNREAD_DATA_CHANGED_EVENT,
      listener,
    );
}

function emitUnreadCount(next: number) {
  const prev = cachedUnreadCount;
  const isFirstInit = !unreadCountInitialized;
  cachedUnreadCount = next;
  unreadCountInitialized = true;
  unreadCountListeners.forEach((listener) => {
    listener(next);
  });
  if (!isFirstInit && prev !== next) {
    notifySiteMessageUnreadDataChanged();
  }
}

/** 拉取并更新全局未读数（并发请求会自动合并） */
export async function refreshSiteMessageUnreadCount(): Promise<number> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const res = await getSiteMessageUnreadCount();
      const next = res?.count ?? 0;
      emitUnreadCount(next);
      return next;
    } catch (error) {
      console.error('获取站内信未读数失败:', error);
      emitUnreadCount(0);
      return 0;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** 标记已读 / 全部已读后刷新未读数 */
export function notifySiteMessageUnreadChanged() {
  void refreshSiteMessageUnreadCount();
}

/** @deprecated 请使用 subscribeSiteMessageUnreadDataChanged */
export function subscribeSiteMessageUnreadChanged(listener: () => void) {
  return subscribeSiteMessageUnreadDataChanged(listener);
}

export function startSiteMessageUnreadPolling() {
  pollRefCount += 1;
  if (pollRefCount === 1) {
    pollTimer = window.setInterval(() => {
      void refreshSiteMessageUnreadCount();
    }, SITE_MESSAGE_UNREAD_POLL_INTERVAL_MS);
  }
}

export function stopSiteMessageUnreadPolling() {
  pollRefCount = Math.max(0, pollRefCount - 1);
  if (pollRefCount === 0 && pollTimer !== null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
}
